import React, {useContext, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import 'styles/menu/mobile/BottomMenuBar.css';
import {UserContext} from 'components/user/UserContext';
import NavigationMenuSvg from 'util/svg/NavigationMenuSvg';
import {useNotification} from 'util/NotificationContext';
import {isOnLocation} from 'util/locationUtil';
import {usePopup} from 'popup/PopupContext';
import AddMoneyPopupWrapper from 'components/donate/AddMoneyPopupWrapper';
import SignInPopup from 'components/login/SignInPopup';
import {useSessionConfiguration} from 'util/SessionConfigurationContext';

const iconSize = 40;
const iconSizePx = '40px';

const minHeight = 87;
const maxHeight = 135;
const dragThreshold = 10;

const MenuLink = ({iconName, isActive, onClick, children, alwaysBlack = false}) => {
    const {user} = useContext(UserContext);

    return (
        <div className={`mobile-menu-link ${isActive}`} onClick={onClick}>
            <NavigationMenuSvg name={iconName} size={iconSize}
                               color={user.isLoggedIn || alwaysBlack ? 'black' : 'gray'}/>
            {children}
        </div>
    );
};

const UserBalance = ({userDollars, userCents, onClick}) => (
    <div
        id={'mobile-user-balance'}
        data-testid="mobile-user-balance"
        onClick={onClick}
        style={{
            height: iconSizePx,
            width: iconSizePx,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
        }}
    >
        {userDollars}
        {userCents > 0 && <small style={{color: '#434343'}}>{userCents}&cent;</small>}
    </div>
);

const BottomMenuBar = () => {
    const navigate = useNavigate();
    const [isEnlarged, setIsEnlarged] = useState(false);
    const [currentHeight, setCurrentHeight] = useState(minHeight);
    const [dragging, setDragging] = useState(false);
    const [startY, setStartY] = useState(0);
    const [dragDistance, setDragDistance] = useState(0);
    const {user} = useContext(UserContext);
    const menuRef = useRef(null);
    const draggableRef = useRef(null);
    const {missedMessages, missedNotifications} = useNotification();
    const location = useLocation();
    const {openPopup} = usePopup();
    const [userCents, setUserCents] = useState(0);
    const [userDollars, setUserDollars] = useState('$?');
    const [signedIn, setSignedIn] = useState(false);
    const {isWideBottom} = useSessionConfiguration();
    const [isTrendingPage, setIsTrendingPage] = useState(location.pathname === '/trending');
    const [isTopPage, setIsTopPage] = useState(location.pathname === '/');

    useEffect(() => {
        if (user.isLoggedIn) {
            setSignedIn(true);
        }
    }, [user.isLoggedIn]);

    useEffect(() => {
        setIsTrendingPage(location.pathname === '/trending');
        setIsTopPage(location.pathname === '/');
    }, [location.pathname]);

    const navigationClick = (path) => {
        if (!signedIn && path !== '/') {
            openPopup(<SignInPopup/>);
            return;
        }
        setCurrentHeight(minHeight);
        navigate(path);
    };

    const isActive = (componentName) => {
        return isOnLocation(location.pathname, componentName, user?.userId) ? 'active' : '';
    };

    const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            setCurrentHeight(minHeight);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleDragStart = (e) => {
        e.preventDefault();
        setDragging(true);
        setDragDistance(0);
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        setStartY(clientY);
    };

    const handleDragMove = (e) => {
        if (!dragging) return;
        e.preventDefault();
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const deltaY = startY - clientY;
        setDragDistance((prev) => prev + Math.abs(deltaY));
        let newHeight = currentHeight + deltaY;
        newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
        setCurrentHeight(newHeight);
        setStartY(clientY);
    };

    const handleDragEnd = (e) => {
        if (!dragging) return;
        e.preventDefault();
        setDragging(false);

        if (dragDistance > dragThreshold) {
            setCurrentHeight(currentHeight > (minHeight + maxHeight) / 2 ? maxHeight : minHeight);
        } else {
            setCurrentHeight(isEnlarged ? minHeight : maxHeight);
        }
    };

    useEffect(() => {
        const element = draggableRef.current;
        if (!element) return;

        element.addEventListener('touchstart', handleDragStart, {passive: false});
        element.addEventListener('touchmove', handleDragMove, {passive: false});
        element.addEventListener('touchend', handleDragEnd);
        element.addEventListener('mousedown', handleDragStart);
        element.addEventListener('mousemove', handleDragMove);
        element.addEventListener('mouseup', handleDragEnd);

        return () => {
            element.removeEventListener('touchstart', handleDragStart);
            element.removeEventListener('touchmove', handleDragMove);
            element.removeEventListener('touchend', handleDragEnd);
            element.removeEventListener('mousedown', handleDragStart);
            element.removeEventListener('mousemove', handleDragMove);
            element.removeEventListener('mouseup', handleDragEnd);
        };
    }, [dragging, startY, dragDistance, isWideBottom]);

    useEffect(() => {
        const element = menuRef.current;
        if (!element) {
            return;
        }

        if (isWideBottom) {
            setIsEnlarged(false);
            setCurrentHeight(minHeight);
            return;
        }

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const h = entry.contentRect.height;
                setIsEnlarged(h > minHeight + 1);
            }
        });

        resizeObserver.observe(element);

        return () => resizeObserver.disconnect();
    }, [isWideBottom]);

    useEffect(() => {
        if (!user.balance) {
            setUserCents(0);
            setUserDollars('$0');
            return;
        }
        const cents = Math.round(user.balance) % 100;
        setUserCents(cents);
        const dollarNumber = Math.floor(user.balance / 100);
        setUserDollars(`$${dollarNumber.toLocaleString()}`);
    }, [user.balance]);

    const loginClick = () => {
        openPopup(<SignInPopup/>);
    };

    const expansionProgress = Math.max(0, Math.min(1, (currentHeight - minHeight) / (maxHeight - minHeight)));

    return (
        <div
            className={`bottom-bar-wrapper ${isEnlarged ? 'enlarged' : ''}`}
            ref={menuRef}
            style={{height: `${currentHeight}px`}}
        >
            <div className="bottom-menu-bar">
                <div className="bottom-menu-first-row">
                    <MenuLink
                        path={isTopPage ? '/trending' : '/'}
                        iconName={isTrendingPage ? 'fire' : 'wall'}
                        alwaysBlack={true}
                        isActive={isActive('the wall') || isActive('trending')}
                        onClick={() => navigationClick(isTopPage ? '/trending' : '/')}
                    />
                    {!signedIn &&
                        <MenuLink iconName={'login'} isActive={''} onClick={loginClick} alwaysBlack={'true'}/>}

                    {signedIn && (
                        <MenuLink
                            path={'/upload'}
                            iconName={'plus'}
                            isActive={isActive('upload')}
                            onClick={() => navigationClick('/upload')}
                        />
                    )}

                    {!isWideBottom && (
                        <div ref={draggableRef} style={{position: 'relative', display: 'flex'}}>
                            <NavigationMenuSvg name={'menu'} size={iconSize}/>
                            {missedNotifications > 0 && (
                                <div className={`small-red-notification-bubble mobile ${isEnlarged ? "hidden" : ""}`}/>
                            )}
                        </div>
                    )}

                    {!signedIn && (
                        <MenuLink
                            path={'/upload'}
                            iconName={'plus'}
                            isActive={isActive('upload')}
                            onClick={() => navigationClick('/upload')}
                        />
                    )}

                    {signedIn && (
                        <MenuLink
                            path={'/chat'}
                            iconName={'chat'}
                            isActive={isActive('chat')}
                            onClick={() => navigationClick('/chat')}
                        >
                            {missedMessages > 0 && (
                                <div className="small-red-notification-bubble mobile message"/>
                            )}
                        </MenuLink>
                    )}

                    {!signedIn && (
                        <MenuLink
                            path={'/chat'}
                            iconName={'chat'}
                            isActive={isActive('chat')}
                            onClick={() => navigationClick('/chat')}
                        >
                            {missedMessages > 0 && (
                                <div className="small-red-notification-bubble mobile message"/>
                            )}
                        </MenuLink>
                    )}

                    {isWideBottom && (
                        <>
                            <MenuLink
                                path={'/mobile-profile'}
                                iconName={'images'}
                                isActive={isActive('profile')}
                                onClick={() => navigationClick('/mobile-profile')}
                            />
                            <MenuLink
                                path={'/lootbox'}
                                iconName={'lootbox'}
                                isActive={isActive('lootbox')}
                                onClick={() => navigationClick('/lootbox')}
                            />
                            <MenuLink
                                path={'/my-items'}
                                iconName={'my-items'}
                                isActive={isActive('inventory')}
                                onClick={() => navigationClick('/my-items')}
                            />
                            <MenuLink
                                path={'/item-store'}
                                iconName={'store'}
                                isActive={isActive('store')}
                                onClick={() => navigationClick('/item-store')}
                            />
                            <MenuLink
                                path={'/mobile-notifications'}
                                iconName={'bell'}
                                isActive={isActive('notifications')}
                                onClick={() => navigationClick('/mobile-notifications')}
                            >
                                {missedNotifications > 0 && (
                                    <div className={`small-red-notification-bubble mobile`}/>
                                )}
                            </MenuLink>
                        </>
                    )}

                    {signedIn && (
                        <UserBalance
                            userDollars={userDollars}
                            userCents={userCents}
                            onClick={() => openPopup(<AddMoneyPopupWrapper/>)}
                        />
                    )}
                </div>
                <div
                    className="bottom-menu-second-row"
                    style={{
                        opacity: expansionProgress,
                        transform: `translateY(${(1 - expansionProgress) * 14}px)`,
                        pointerEvents: expansionProgress > 0.15 ? 'auto' : 'none',
                    }}
                >
                    <MenuLink
                        path={'/lootbox'}
                        iconName={'lootbox'}
                        isActive={isActive('lootbox')}
                        onClick={() => navigationClick('/lootbox')}
                    />
                    <MenuLink
                        path={'/item-store'}
                        iconName={'store'}
                        isActive={isActive('store')}
                        onClick={() => navigationClick('/item-store')}
                    />
                    <MenuLink
                        path={'/mobile-profile'}
                        iconName={'images'}
                        isActive={isActive('profile')}
                        onClick={() => navigationClick('/mobile-profile')}
                    />
                    <MenuLink
                        path={'/my-items'}
                        iconName={'my-items'}
                        isActive={isActive('inventory')}
                        onClick={() => navigationClick('/my-items')}
                    />
                    <MenuLink
                        path={'/mobile-notifications'}
                        iconName={'bell'}
                        isActive={isActive('notifications')}
                        onClick={() => navigationClick('/mobile-notifications')}
                    >
                        {missedNotifications > 0 && (
                            <div
                                className={`small-red-notification-bubble mobile ${expansionProgress > 0.85 ? "" : "hidden"}`}/>
                        )}
                    </MenuLink>
                </div>
            </div>
        </div>
    );
};

export default BottomMenuBar;
